import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { X, LogIn } from 'lucide-react';
import { api, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import ListCardMosaic from '@/components/lists/ListCardMosaic';
import { publicListaToListCard } from '@/components/lists/listCardUtils';
import FormatoPicker from '@/components/FormatoPicker';
import { List, User } from 'lucide-react';
import { SegmentedTabSwitch } from '@/components/ui/SegmentedTabSwitch';

const FORMATO_IDS_VALIDOS = new Set([1, 3, 4, 5, 8, 9, 10, 13]);
const FORMATO_PADRAO_COMIC = 5;

function parseFormatoQuery(raw: string | null): number {
    if (raw == null || raw === '') return FORMATO_PADRAO_COMIC;
    const n = Number(raw);
    if (!Number.isInteger(n) || !FORMATO_IDS_VALIDOS.has(n)) return FORMATO_PADRAO_COMIC;
    return n;
}

interface PublicLista {
    id: number;
    nome: string;
    descricao?: string | null;
    publica: boolean;
    criada_em: string;
    usuario: { id?: number; nick: string; nome?: string; imagem?: string | null };
    total_obras: number;
    total_seguidores: number;
    total_curtidas?: number;
    /** Presente quando o utilizador está autenticado e a API devolve estado social. */
    seguindo?: boolean;
    obras?: { id?: number; obr_id?: number; nome?: string; imagem?: string | null; capa?: string | null }[];
}

function normalizeListasArray(raw: unknown): PublicLista[] {
    if (Array.isArray(raw)) return raw as PublicLista[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
        return (raw as { data: PublicLista[] }).data;
    }
    return [];
}

type ListaScope = 'todos' | 'seguidas';
type ListaOrdenar = 'seguidores_desc' | 'recentes' | 'curtidas_desc';

const ORDENAR_OPTIONS: { value: ListaOrdenar; label: string }[] = [
    { value: 'seguidores_desc', label: 'Mais seguidas' },
    { value: 'recentes', label: 'Novas' },
    { value: 'curtidas_desc', label: 'Mais curtidas' },
];

function ListasGridSkeleton() {
    return (
        <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5 lg:grid-cols-4"
            aria-hidden
        >
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3">
                    <div className="aspect-[1/1] w-full animate-pulse rounded-xl bg-white/[0.06] md:aspect-[3/4]" />
                    <div className="flex items-center justify-between gap-2 border-t border-white/5 px-1 pt-3">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <div className="size-8 shrink-0 animate-pulse rounded-full bg-white/[0.06]" />
                            <div className="h-3 flex-1 max-w-[8rem] animate-pulse rounded bg-white/[0.06]" />
                        </div>
                        <div className="h-9 w-[5.5rem] shrink-0 animate-pulse rounded-lg bg-white/[0.06]" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ListasPage() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [scope, setScope] = useState<ListaScope>('todos');
    const [ordenar, setOrdenar] = useState<ListaOrdenar>('seguidores_desc');
    const [formatoId, setFormatoId] = useState(() => parseFormatoQuery(searchParams.get('formato')));
    const [listas, setListas] = useState<PublicLista[]>([]);
    const [loading, setLoading] = useState(true);
    const [loginModalOpen, setLoginModalOpen] = useState(false);

    useEffect(() => {
        const next = parseFormatoQuery(searchParams.get('formato'));
        setFormatoId((prev) => (prev === next ? prev : next));
    }, [searchParams]);

    useEffect(() => {
        const params = new URLSearchParams(searchParams.toString());
        if (params.get('formato') === String(formatoId)) return;
        params.set('formato', String(formatoId));
        const q = params.toString();
        navigate({ pathname: '/listas', search: q ? `?${q}` : '' }, { replace: true });
    }, [formatoId, navigate, searchParams]);

    const fetchPublicListas = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('ordenar', ordenar);
            params.set('formato', String(formatoId));
            const res = await api.get(`listas/publicas?${params.toString()}`);
            const list = normalizeListasArray(unwrapApiResponse<unknown>(res));
            setListas(list);
        } catch (error) {
            console.error('Erro ao buscar listas públicas:', error);
            setListas([]);
        } finally {
            setLoading(false);
        }
    }, [formatoId, ordenar]);

    const listasVisiveis = useMemo(() => {
        if (scope !== 'seguidas') return listas;
        return listas.filter((l) => Boolean(l.seguindo));
    }, [listas, scope]);

    useEffect(() => {
        void fetchPublicListas();
    }, [fetchPublicListas, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) setScope('todos');
    }, [isAuthenticated]);

    useEffect(() => {
        if (!loginModalOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') setLoginModalOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [loginModalOpen]);

    const handleFollow = async (e: React.MouseEvent, listaId: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isAuthenticated) {
            setLoginModalOpen(true);
            return;
        }
        try {
            await api.post(`listas/${listaId}/seguir`, {}, { requiresAuth: true });
            await fetchPublicListas();
        } catch (error) {
            console.error('Erro ao seguir/deixar de seguir lista:', error);
        }
    };

    return (
        <div className="min-h-screen bg-black pb-24 text-white md:pb-10">
            <Navbar />
            <main className="mx-auto max-w-7xl px-4 pt-4 lg:pt-36">
                <div className="mb-10 flex flex-col gap-6 px-2">
                    <div className="flex items-center gap-4">
                        <div className="h-10 w-1.5 shrink-0 rounded-full bg-orange-600" />
                        <div>
                            <h1 className="text-2xl font-black uppercase italic tracking-tighter text-white md:text-4xl">Listas</h1>
                            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.3em] text-gray-500">Descubra coleções da comunidade</p>
                        </div>
                    </div>

                    <div className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
                        <SegmentedTabSwitch<ListaScope>
                            className="w-full shrink-0 md:w-auto md:min-w-[13rem]"
                            value={scope}
                            onChange={setScope}
                            ariaLabel="Filtrar listas"
                            options={[
                                { value: 'todos', label: 'Todas' },
                                {
                                    value: 'seguidas',
                                    label: 'Seguidas',
                                    disabled: !isAuthenticated,
                                    title: !isAuthenticated ? 'Entre para ver as listas que segue' : undefined,
                                },
                            ]}
                        />
                        <div className="min-w-0 flex-1 md:flex md:justify-end">
                            <FormatoPicker
                                desktopAlign="end"
                                onSelect={setFormatoId}
                                selectedFormato={formatoId}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {ORDENAR_OPTIONS.map(({ value, label }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setOrdenar(value)}
                                className={`px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-wider transition-all duration-200 ${
                                    ordenar === value
                                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                                        : 'bg-white/[0.06] text-white/50 hover:bg-white/[0.10] hover:text-white/80'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="px-2">
                        <p className="mb-6 text-center text-xs font-bold uppercase tracking-widest text-neutral-500 md:text-left">
                            Carregando listas…
                        </p>
                        <ListasGridSkeleton />
                    </div>
                ) : listasVisiveis.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-[2.5rem] border border-dashed border-neutral-800 bg-neutral-900/20 py-32">
                        <List className="mb-6 size-16 text-neutral-800" />
                        <h3 className="text-xl font-bold text-neutral-400">
                            {scope === 'seguidas' && listas.length > 0
                                ? 'Nenhuma lista seguida aqui'
                                : 'Nenhuma lista encontrada'}
                        </h3>
                        <p className="mb-8 max-w-sm text-center font-medium text-neutral-600">
                            {scope === 'seguidas' && !isAuthenticated
                                ? 'Entre na sua conta para ver e gerir as listas que segue.'
                                : scope === 'seguidas' && listas.length > 0
                                  ? 'Explore “Todas” e siga listas da comunidade.'
                                  : 'Tente outro formato ou explore as sugestões.'}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setScope('todos');
                                setFormatoId(FORMATO_PADRAO_COMIC);
                                void fetchPublicListas();
                            }}
                            className="rounded-xl bg-neutral-800 px-8 py-3 text-sm font-bold text-white transition-all hover:bg-neutral-700"
                        >
                            Ver todas (Comic)
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
                        {listasVisiveis.map((lista) => (
                            <div key={lista.id} className="flex flex-col gap-3">
                                <ListCardMosaic list={publicListaToListCard(lista)} to={`/listas/${lista.id}`} />
                                <div className="flex items-center justify-between border-t border-white/10 px-1 pt-3">
                                    <Link
                                        to={`/listas/${lista.id}`}
                                        className="flex min-w-0 flex-1 items-center gap-2 pr-2 text-left no-underline"
                                    >
                                        <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
                                            <User className="size-3.5 text-white/50" />
                                        </div>
                                        <span className="truncate text-xs font-semibold text-white/55 transition-colors hover:text-white">
                                            {lista.usuario.nick}
                                        </span>
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={(e) => handleFollow(e, lista.id)}
                                        className={`shrink-0 rounded-lg px-4 py-2 text-xs font-bold transition-all ${
                                            lista.seguindo
                                                ? 'border border-white/10 bg-white/[0.06] text-white/55 hover:bg-white/[0.08]'
                                                : 'bg-orange-600 text-white shadow-lg shadow-orange-900/20 hover:bg-orange-500'
                                        }`}
                                    >
                                        {lista.seguindo ? 'Seguindo' : 'Seguir'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <MobileBottomNav />

            {loginModalOpen ? (
                <div
                    className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center sm:p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="listas-login-modal-title"
                >
                    <button
                        type="button"
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        aria-label="Fechar"
                        onClick={() => setLoginModalOpen(false)}
                    />
                    <div className="relative w-full max-w-md overflow-hidden rounded-t-[1.35rem] border border-white/10 border-b-0 bg-neutral-950 shadow-2xl animate-in fade-in slide-in-from-bottom-3 duration-200 sm:rounded-2xl sm:border-b">
                        <div className="h-1 w-full bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500/80" />
                        <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
                            <div className="min-w-0">
                                <h2 id="listas-login-modal-title" className="text-lg font-bold text-white">
                                    Entre para seguir listas
                                </h2>
                                <p className="mt-2 text-sm leading-relaxed text-white/50">
                                    Crie uma conta ou inicie sessão para guardar listas da comunidade e vê-las no separador «Seguidas».
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setLoginModalOpen(false)}
                                className="shrink-0 rounded-lg p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-white"
                                aria-label="Fechar"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex flex-col gap-2 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4 sm:flex-row sm:pb-5">
                            <button
                                type="button"
                                onClick={() => setLoginModalOpen(false)}
                                className="rounded-xl border border-white/10 py-3 text-sm font-semibold text-white/70 transition-colors hover:bg-white/[0.04] sm:flex-1"
                            >
                                Agora não
                            </button>
                            <Link
                                to="/entrar"
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-600 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-500"
                            >
                                <LogIn size={18} aria-hidden />
                                Entrar
                            </Link>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
